/**
 * Kampf-Tracker: reine Logik (Sortierung, Zugwechsel, Trefferpunkte, Zustände).
 * Gewürfelt wird mit echten Würfeln – hier werden nur Ergebnisse verarbeitet.
 */
import { naturalCompare } from './naturalSort.js';
import { STATBLOCK_DE } from './statblockText.js';

/** Anzeigename eines Zustands (deutsch, freie Texte unverändert). */
export function conditionName(key: string): string {
  return STATBLOCK_DE.conditions[key.toLowerCase()] ?? key;
}

export type CombatantKind = 'pc' | 'npc' | 'monster' | 'other';
export type CombatStatus = 'prep' | 'active' | 'ended';

export interface CombatCondition {
  /** Schlüssel aus `CONDITIONS` (z. B. `poisoned`) oder freier Text. */
  key: string;
  /** Verbleibende Runden; null = bis auf Weiteres. */
  rounds: number | null;
  note?: string;
}

export interface CombatantState {
  id: string;
  name: string;
  kind: CombatantKind;
  noteId: string | null;
  initiative: number | null;
  initBonus: number;
  sortOrder: number;
  hpCurrent: number | null;
  hpMax: number | null;
  tempHp: number;
  ac: number | null;
  conditions: CombatCondition[];
  concentration: boolean;
  defeated: boolean;
  /** Todesrettungswürfe (SC und NSC bei 0 TP). */
  deathSaves: DeathSaves;
  hidden: boolean;
  groupId: string | null;
  notes: string;
}

export interface DeathSaves {
  success: number;
  failure: number;
}

export const NO_DEATH_SAVES: DeathSaves = { success: 0, failure: 0 };

/** SC und NSC fallen bei 0 TP bewusstlos um und machen Todesrettungswürfe; Monster sind besiegt. */
export function makesDeathSaves(c: Pick<CombatantState, 'kind'>): boolean {
  return c.kind === 'pc' || c.kind === 'npc';
}

export interface CombatTurnState {
  status: CombatStatus;
  round: number;
  activeCombatantId: string | null;
}

/** Initiative-Reihenfolge: höchste zuerst, Gleichstand über Bonus, dann manuelle Reihenfolge. */
export function sortCombatants<T extends Pick<CombatantState, 'initiative' | 'initBonus' | 'sortOrder' | 'name'>>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => {
    if (a.initiative === null && b.initiative !== null) return 1;
    if (b.initiative === null && a.initiative !== null) return -1;
    if (a.initiative !== null && b.initiative !== null && a.initiative !== b.initiative) return b.initiative - a.initiative;
    if (a.initBonus !== b.initBonus) return b.initBonus - a.initBonus;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return naturalCompare(a.name, b.name);
  });
}

export interface TurnEvent {
  type: 'round' | 'turn' | 'condition-expired';
  combatantId?: string;
  text: string;
}

export interface TurnResult {
  state: CombatTurnState;
  /** Geänderte Zustände je Teilnehmer (nur bei Vorwärts-Zugwechsel). */
  conditionUpdates: Map<string, CombatCondition[]>;
  events: TurnEvent[];
}

/**
 * Nächster bzw. vorheriger Zug. Zustände mit Rundendauer zählen am Ende
 * des Zuges der betroffenen Kreatur herunter und laufen bei 0 aus.
 * Besiegte Teilnehmer werden übersprungen.
 */
export function advanceTurn(list: readonly CombatantState[], state: CombatTurnState, direction: 1 | -1 = 1): TurnResult {
  const order = sortCombatants(list).filter((c) => !c.defeated);
  const conditionUpdates = new Map<string, CombatCondition[]>();
  const events: TurnEvent[] = [];
  if (order.length === 0) return { state: { ...state }, conditionUpdates, events };

  const idx = order.findIndex((c) => c.id === state.activeCombatantId);
  if (idx === -1) {
    // Start oder aktiver Teilnehmer wurde entfernt/besiegt
    const round = Math.max(1, state.round);
    const first = order[0]!;
    events.push({ type: 'turn', combatantId: first.id, text: `${first.name} ist am Zug` });
    return { state: { status: 'active', round, activeCombatantId: first.id }, conditionUpdates, events };
  }

  if (direction === 1) {
    const current = order[idx]!;
    if (current.conditions.some((c) => c.rounds !== null)) {
      const kept: CombatCondition[] = [];
      for (const c of current.conditions) {
        if (c.rounds === null) kept.push(c);
        else if (c.rounds > 1) kept.push({ ...c, rounds: c.rounds - 1 });
        else events.push({ type: 'condition-expired', combatantId: current.id, text: `${current.name}: ${conditionName(c.key)} endet` });
      }
      conditionUpdates.set(current.id, kept);
    }
  }

  let next = idx + direction;
  let round = state.round;
  if (next >= order.length) {
    next = 0;
    round += 1;
    events.push({ type: 'round', text: `Runde ${round}` });
  } else if (next < 0) {
    next = order.length - 1;
    round = Math.max(1, round - 1);
  }
  const nc = order[next]!;
  events.push({ type: 'turn', combatantId: nc.id, text: `${nc.name} ist am Zug` });
  return { state: { status: 'active', round, activeCombatantId: nc.id }, conditionUpdates, events };
}

export type HpChangeKind = 'damage' | 'heal' | 'temp' | 'set';

/** Schnelleingabe: `7` / `-7` Schaden, `+5` Heilung, `t8` temporäre TP, `=20` setzen. */
export function parseHpInput(input: string): { kind: HpChangeKind; amount: number } | null {
  const s = input.trim().toLowerCase().replace(/\s+/g, '');
  let m = /^(?:t|temp|tmp)(\d+)$/.exec(s);
  if (m) return { kind: 'temp', amount: Number(m[1]) };
  m = /^=(\d+)$/.exec(s);
  if (m) return { kind: 'set', amount: Number(m[1]) };
  m = /^\+(\d+)$/.exec(s);
  if (m) return { kind: 'heal', amount: Number(m[1]) };
  m = /^-?(\d+)$/.exec(s);
  if (m) return { kind: 'damage', amount: Number(m[1]) };
  return null;
}

/** SG des Konzentrationsrettungswurfs: halber Schaden, mindestens 10 (2024: höchstens 30). */
export function concentrationDc(damage: number, ruleset: '2014' | '2024' = '2014'): number {
  const dc = Math.max(10, Math.floor(damage / 2));
  return ruleset === '2024' ? Math.min(30, dc) : dc;
}

/** Zustand „verstorben“: bleibt bei Heilung bestehen und nimmt aus der Zugreihenfolge. */
export const DEAD = 'dead';

export function isDead(c: Pick<CombatantState, 'conditions'>): boolean {
  return c.conditions.some((x) => x.key === DEAD);
}

/** Markiert als verstorben (bzw. hebt es auf). Ohne Tod gilt nur ein Monster mit 0 TP als besiegt. */
export function deadPatch(c: CombatantState, dead: boolean): Pick<CombatantState, 'conditions' | 'defeated'> {
  if (dead) {
    return { conditions: [...c.conditions.filter((x) => x.key !== DEAD && x.key !== 'unconscious'), { key: DEAD, rounds: null }], defeated: true };
  }
  const hp = c.hpCurrent ?? c.hpMax;
  return { conditions: c.conditions.filter((x) => x.key !== DEAD), defeated: !makesDeathSaves(c) && hp === 0 };
}

type CombatantPatch = Partial<Pick<CombatantState, 'hpCurrent' | 'tempHp' | 'defeated' | 'conditions' | 'deathSaves'>>;

/**
 * Setzt Todesrettungswürfe (0–3). Drei Fehlschläge: verstorben. Drei Erfolge: stabil.
 */
export function applyDeathSaves(c: CombatantState, saves: DeathSaves): { patch: CombatantPatch; events: string[] } {
  const next = {
    success: Math.min(3, Math.max(0, Math.floor(saves.success))),
    failure: Math.min(3, Math.max(0, Math.floor(saves.failure))),
  };
  const patch: CombatantPatch = { deathSaves: next };
  const events: string[] = [];
  if (isDead(c)) return { patch, events };
  if (next.failure >= 3 && c.deathSaves.failure < 3) {
    Object.assign(patch, deadPatch(c, true));
    events.push(`${c.name} stirbt (drei misslungene Todesrettungswürfe)`);
  } else if (next.success >= 3 && c.deathSaves.success < 3) {
    events.push(`${c.name} ist stabil (drei erfolgreiche Todesrettungswürfe)`);
  }
  return { patch, events };
}

export interface HpChangeResult {
  patch: CombatantPatch;
  /** SG, falls ein Konzentrationswurf fällig ist. */
  concentrationDc: number | null;
  events: string[];
}

/**
 * Wendet Schaden/Heilung an. Schaden trifft zuerst temporäre TP.
 * Monster bei 0 TP gelten als besiegt, SC und NSC werden bewusstlos und machen
 * Todesrettungswürfe; Schaden bei 0 TP zählt als ein Fehlschlag.
 * Massiver Schaden: Bleibt nach dem Fall auf 0 TP Schaden übrig, der mindestens
 * dem TP-Maximum entspricht, ist das Ziel sofort tot (gilt auch, wenn es schon bei 0 TP war).
 * Verstorbene werden durch Heilung nicht wieder aufgeweckt.
 */
export function applyHpChange(c: CombatantState, kind: HpChangeKind, amount: number, ruleset: '2014' | '2024' = '2014'): HpChangeResult {
  const events: string[] = [];
  const patch: HpChangeResult['patch'] = {};
  const max = c.hpMax ?? Number.POSITIVE_INFINITY;
  const cur = c.hpCurrent ?? c.hpMax ?? 0;
  let dc: number | null = null;
  amount = Math.max(0, Math.floor(amount));

  if (kind === 'temp') {
    // Temporäre TP addieren sich nicht – der höhere Wert gilt
    patch.tempHp = Math.max(c.tempHp, amount);
    events.push(`${c.name} erhält ${amount} temporäre TP`);
    return { patch, concentrationDc: null, events };
  }

  if (kind === 'damage') {
    const absorbed = Math.min(c.tempHp, amount);
    const rest = amount - absorbed;
    if (absorbed > 0) patch.tempHp = c.tempHp - absorbed;
    const hp = Math.max(0, cur - rest);
    patch.hpCurrent = hp;
    events.push(`${c.name} erleidet ${amount} Schaden${absorbed ? ` (${absorbed} temporär)` : ''} → ${hp} TP`);
    if (c.concentration && amount > 0) dc = concentrationDc(amount, ruleset);
    if (isDead(c)) return { patch, concentrationDc: dc, events };
    // Schaden, der nach dem Fall auf 0 TP übrig bleibt
    const overflow = rest - cur;
    if (hp === 0 && rest > 0 && c.hpMax !== null && overflow >= c.hpMax) {
      Object.assign(patch, deadPatch(c, true));
      events.push(`${c.name} stirbt durch massiven Schaden (${overflow} Schaden über 0 TP, TP-Maximum ${c.hpMax})`);
      return { patch, concentrationDc: dc, events };
    }
    if (cur === 0 && rest > 0 && makesDeathSaves(c)) {
      events.push(`${c.name} erleidet Schaden bei 0 TP: ein misslungener Todesrettungswurf (bei kritischem Treffer zwei)`);
      const ds = applyDeathSaves(c, { ...c.deathSaves, failure: c.deathSaves.failure + 1 });
      Object.assign(patch, ds.patch);
      events.push(...ds.events);
    }
    if (hp === 0 && cur > 0) {
      if (makesDeathSaves(c)) {
        if (!c.conditions.some((x) => x.key === 'unconscious')) patch.conditions = [...c.conditions, { key: 'unconscious', rounds: null }];
        if (c.deathSaves.success || c.deathSaves.failure) patch.deathSaves = { ...NO_DEATH_SAVES };
        events.push(`${c.name} ist bewusstlos`);
      } else {
        patch.defeated = true;
        events.push(`${c.name} ist besiegt`);
      }
    }
    return { patch, concentrationDc: dc, events };
  }

  const target = kind === 'set' ? amount : cur + amount;
  const hp = Math.min(max, Math.max(0, target));
  patch.hpCurrent = hp;
  events.push(kind === 'set' ? `${c.name}: TP auf ${hp} gesetzt` : `${c.name} wird um ${amount} geheilt → ${hp} TP`);
  if (hp > 0 && !isDead(c)) {
    if (c.defeated) patch.defeated = false;
    if (c.deathSaves.success || c.deathSaves.failure) patch.deathSaves = { ...NO_DEATH_SAVES };
    if (c.conditions.some((x) => x.key === 'unconscious') && cur === 0) {
      patch.conditions = c.conditions.filter((x) => x.key !== 'unconscious');
    }
  }
  return { patch, concentrationDc: null, events };
}

/** Name für weitere gleiche Gegner: „Skelett 3“, wenn „Skelett 1“ und „2“ existieren. */
export function numberedNames(base: string, count: number, existing: readonly string[]): string[] {
  if (count <= 1 && !existing.some((n) => n === base || n.startsWith(`${base} `))) return [base];
  const re = new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} (\\d+)$`);
  let max = 0;
  for (const n of existing) {
    const m = re.exec(n);
    if (m) max = Math.max(max, Number(m[1]));
    else if (n === base) max = Math.max(max, 1);
  }
  return Array.from({ length: count }, (_, i) => `${base} ${max + i + 1}`);
}
